import { useState } from 'react';
import { TbEye, TbEyeOff } from 'react-icons/tb';
import Tooltip from 'react-tooltip';
import { DataGrid } from '@/components/data-grid';
import { Button } from '@/components/ui/button';
import type { OtaiLowestRatesQuery } from '@/features/overview-table/gql/_gen_/rate-shop.gql';
import type { CellObject, HeaderObject } from '@/features/overview-table/hooks/use-table';
import { DateHelpers } from '../../../helpers/date-helpers';
import type { DailyRatesData } from '../context/report-data-context';
import { RateDataTable } from './rate-data-table';

type DetailTableProps = {
  brandCode: string;
  handleTableChange: (tableVersion: string) => void;
  headers: HeaderObject[];
  rows: CellObject[][];
  tableVersion: string;
  todaysDate?: string;
  rateShopData?: OtaiLowestRatesQuery;
  lowestRates?: DailyRatesData;
  flexRates?: DailyRatesData;
  loadingRateShop?: boolean;
  meetingCategory?: string;
};

export const DetailTable = ({
  brandCode,
  handleTableChange,
  headers,
  rows,
  tableVersion,
  todaysDate = DateHelpers.today(),
  rateShopData,
  lowestRates,
  flexRates,
  loadingRateShop = false,
  meetingCategory,
}: DetailTableProps) => {
  const toggleableColumns = ['occ', 'occ_rc'];
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    headers.map((header) => header.dataKey),
  );
  const toggleColumn = (colKey: string) => {
    setVisibleColumns((prev: string[]) =>
      prev.includes(colKey) ? prev.filter((col) => col !== colKey) : [...prev, colKey],
    );
  };
  const filteredHeaders = headers.filter((header) => visibleColumns.includes(header.dataKey));
  const filteredRows = rows.map((row) =>
    row.filter((cell) => visibleColumns.includes(cell.dataKey)),
  );
  return (
    <>
      <nav className='mb-2'>
        <div className='flex items-center gap-4'>
          <div className='flex gap-1 mt-2'>
            <Button
              variant={tableVersion === 'detail' ? 'default' : 'secondary'}
              onClick={() => handleTableChange('detail')}
              disabled={tableVersion === 'detail'}
            >
              Detail Table
            </Button>
            <Button
              variant={tableVersion === 'rates' ? 'default' : 'secondary'}
              onClick={() => handleTableChange('rates')}
              disabled={tableVersion === 'rates'}
            >
              Rate Data
            </Button>
          </div>
          {tableVersion === 'detail' ? (
            <div className='flex gap-2 ml-64 mt-2'>
              {headers
                .filter((header) => toggleableColumns.includes(header.dataKey))
                .map((header) => (
                  <Button
                    key={header.dataKey}
                    onClick={() => toggleColumn(header.dataKey)}
                    variant={'outline'}
                    data-tip={
                      visibleColumns.includes(header.dataKey)
                        ? `Hide ${header.title}`
                        : `Show ${header.title}`
                    }
                  >
                    {visibleColumns.includes(header.dataKey) ? (
                      <TbEyeOff className='w-4 h-4 text-blue-900' />
                    ) : (
                      <TbEye className='w-4 h-4 text-gray-400' />
                    )}
                    {header.title}
                  </Button>
                ))}
              <Tooltip />
            </div>
          ) : null}
        </div>
      </nav>

      {tableVersion === 'detail' ? (
        <DataGrid
          brandCode={brandCode}
          colToggles={{}}
          dateSegment='day'
          headers={filteredHeaders}
          monthOffset={0}
          rows={filteredRows}
          snapshotDate={todaysDate}
          sortDir='asc'
          sortKey='stay_date'
          targetDate={{
            value: todaysDate,
          }}
          startDate={DateHelpers.subtract(todaysDate, 'month', 1)}
          setSortDir={() => {}}
          setSortKey={() => {}}
        />
      ) : (
        <RateDataTable
          brandCode={brandCode}
          rateShopData={rateShopData}
          lowestRates={lowestRates}
          flexRates={flexRates}
          loading={loadingRateShop}
          meetingCategory={meetingCategory}
        />
      )}
    </>
  );
};
